// Hợp đồng của attendance-service (đặc tả docs/backend/services/attendance-service.md): vào/ra ca (giờ máy chủ),
// điều chỉnh ngày công có giải trình, đơn nghỉ, bảng công tháng, chốt/mở lại bảng công (bên tham gia saga duyệt kỳ
// chi trả của commission). Luật đánh giá ngày/tháng dùng packages/domain (evalDay, monthSheet, stdDays…).
// Quy ước: tiền VND số nguyên (number); ngày YYYY-MM-DD, tháng YYYY-MM theo Asia/Ho_Chi_Minh; thời điểm ISO 8601 UTC.
import { z } from 'zod';

// ---------- Bộ dựng schema (thông điệp tiếng Việt) ----------
const REQUIRED = 'Thiếu dữ liệu bắt buộc';
const msg = (message: string) => ({ errorMap: () => ({ message }) });
const str = (required = REQUIRED) => z.string({ required_error: required, invalid_type_error: 'Phải là chuỗi ký tự' });
const text = (max = 200) => str().trim().max(max, `Tối đa ${max} ký tự`);
const reqText = (max: number, message: string) => str(message).trim().min(1, message).max(max, `Tối đa ${max} ký tự`);
const uuid = str().uuid('Mã định danh không hợp lệ');
const int = () => z.number({ required_error: REQUIRED, invalid_type_error: 'Phải là số' }).int('Phải là số nguyên');
const version = int().min(0, 'Phiên bản không hợp lệ');
const bool = z.boolean({ required_error: REQUIRED, invalid_type_error: 'Phải là đúng/sai' });
const isRealDate = (s: string) => {
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
};
const dateKeyReq = (required = REQUIRED) => str(required).regex(/^\d{4}-\d{2}-\d{2}$/, 'Ngày dạng YYYY-MM-DD').refine(isRealDate, 'Ngày không tồn tại');
const dateKey = dateKeyReq();
const monthKeyReq = (required = 'Chọn tháng') => str(required).regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Tháng dạng YYYY-MM');
const monthKey = monthKeyReq();
const isoTime = str().datetime({ offset: true, message: 'Thời điểm phải theo ISO 8601' });
const qInt = (min: number, max: number, def?: number) => {
  const base = z.coerce.number({ invalid_type_error: 'Phải là số' }).int('Phải là số nguyên').min(min, `Tối thiểu ${min}`).max(max, `Tối đa ${max}`);
  return def === undefined ? base.optional() : base.default(def);
};
const boolish = z.preprocess(
  (v) => (v === 'true' || v === '1' || v === true ? true : v === 'false' || v === '0' || v === false ? false : undefined),
  z.boolean().optional(),
);
const optText = (max = 200) => z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? undefined : v), text(max).optional());

// ---------- Hằng số (nhãn/mã/công ở packages/domain LEAVE_TYPES) ----------
export const LEAVE_TYPES = ['annual', 'unpaid', 'sick', 'business'] as const;
export type LeaveType = (typeof LEAVE_TYPES)[number];
export const LEAVE_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type LeaveStatus = (typeof LEAVE_STATUSES)[number];
/** Địa điểm làm việc (enum production; nhãn hiển thị như prototype PLACES). */
export const PLACES = ['office', 'project_duty', 'customer_meeting', 'sales_event'] as const;
export type Place = (typeof PLACES)[number];
export const PLACE_LABEL: Record<Place, string> = {
  office: 'Văn phòng',
  project_duty: 'Trực dự án',
  customer_meeting: 'Gặp khách',
  sales_event: 'Sự kiện mở bán',
};
/** Mã ngày trên bảng công (evalDay). */
export const DAY_CODES = ['X', 'M', 'S', '½', '½?', 'V', '•', 'P', 'KL', 'Ô', 'CT', 'ĐC', '½ĐC', ''] as const;
export type DayCode = (typeof DAY_CODES)[number];
export const DAY_STATUSES = ['ok', 'late', 'early', 'working', 'missingOut', 'absent', 'off', 'future', 'leave', 'adjusted'] as const;
export type DayStatus = (typeof DAY_STATUSES)[number];
/** Công được tính khi điều chỉnh ⇒ mã ĐC / ½ĐC / V. */
export const ADJUST_CREDITS = [1, 0.5, 0] as const;
export type AdjustCredit = (typeof ADJUST_CREDITS)[number];
export const PERIOD_STATUSES = ['open', 'finalized'] as const;
export type PeriodStatus = (typeof PERIOD_STATUSES)[number];
export const PAYOUT_STATUSES = ['none', 'draft', 'approved', 'paid'] as const;
export type PayoutStatusMirror = (typeof PAYOUT_STATUSES)[number];
export const RECALC_CAUSES = ['check_in', 'check_out', 'adjustment', 'leave_approved', 'policy_changed', 'day_rollover', 'roster_changed', 'reopened'] as const;
export type RecalcCause = (typeof RECALC_CAUSES)[number];
export const FINALIZE_TRIGGERS = ['manual', 'payout_saga'] as const;
export type FinalizeTrigger = (typeof FINALIZE_TRIGGERS)[number];
/** Nghỉ trưa cố định (không cấu hình — identity §2.2). */
export const LUNCH = { from: '12:00', to: '13:30', minutes: 90 } as const;

// ---------- Lệnh người dùng ----------
/** Vào ca: giờ do máy chủ đóng dấu; clientTime chỉ để chẩn đoán lệch giờ. projectId chỉ nhận khi place = project_duty. */
export const checkInInput = z.object({
  place: z.enum(PLACES, msg('Địa điểm không hợp lệ')).default('office'),
  projectId: uuid.nullish(),
  note: optText(500),
  clientTime: isoTime.optional(),
});
/** Ra ca sớm cần acknowledgeEarly = true (lần đầu ⇒ 422 EARLY_CHECKOUT_UNCONFIRMED kèm earlyMinutes, credit). */
export const checkOutInput = z.object({
  note: optText(500),
  acknowledgeEarly: bool.default(false),
  clientTime: isoTime.optional(),
});
export const adjustWorkDayInput = z.object({
  staffId: uuid,
  workDate: dateKeyReq('Chọn ngày'),
  credit: z.union([z.literal(1), z.literal(0.5), z.literal(0)], msg('Công được tính là 1, 0,5 hoặc 0')),
  reason: reqText(1000, 'Nhập giải trình'),
  expectedVersion: version.optional(),
});
/** half ⇒ toDate := fromDate (toDate gửi lên bị bỏ qua); thiếu toDate ⇒ = fromDate. */
export const submitLeaveRequestInput = z
  .object({
    type: z.enum(LEAVE_TYPES, msg('Loại nghỉ không hợp lệ')).default('annual'),
    fromDate: dateKeyReq('Chọn từ ngày'),
    toDate: dateKey.optional(),
    half: bool.default(false),
    reason: reqText(1000, 'Nhập lý do'),
  })
  .transform((v) => ({ ...v, toDate: v.half || !v.toDate ? v.fromDate : v.toDate }))
  .refine((v) => v.fromDate <= v.toDate, { message: 'Đến ngày phải sau hoặc bằng từ ngày', path: ['toDate'] });
export const leaveRef = z.object({ leaveId: uuid });
export const approveLeaveRequestInput = z.object({ leaveId: uuid, expectedVersion: version.optional() });
export const rejectLeaveRequestInput = z.object({ leaveId: uuid, note: reqText(1000, 'Nhập lý do từ chối'), expectedVersion: version.optional() });
/** Chốt tháng đã kết thúc (attendance.adjust phạm vi all). */
export const finalizeTimesheetInput = z.object({
  month: monthKey,
  acknowledgePendingLeaves: bool.default(false),
  note: optText(500),
});
export const reopenTimesheetInput = z.object({ month: monthKey, reason: reqText(500, 'Nhập lý do mở lại') });
/** Trả text/csv bang-cong-{YYYY-MM}.csv. */
export const exportTimesheetInput = z.object({ month: monthKey, teamId: uuid.optional() });

// ---------- Truy vấn công khai ----------
export const getShiftPolicyInput = z.object({});
export const getMyTodayInput = z.object({});
export const getMyTimesheetInput = z.object({ month: monthKey.optional() });
export const getAnnualLeaveBalanceInput = z.object({ year: qInt(2000, 2100) });
export const getTeamTimesheetInput = z.object({
  month: monthKey,
  teamId: uuid.optional(),
  q: text(200).optional(),
  includeLeft: boolish,
});
export const getWorkDayDetailInput = z.object({ staffId: uuid, workDate: dateKey });
export const listLeaveRequestsInput = z.object({
  /** all = tất cả trạng thái. */
  status: z.enum(['pending', 'approved', 'rejected', 'all'], msg('Trạng thái không hợp lệ')).default('pending'),
  page: qInt(1, 100_000, 1),
  limit: qInt(1, 200, 50),
});
export const countPendingLeavesInput = z.object({});
export const getTodayPresenceSummaryInput = z.object({ scope: z.enum(['mine', 'tenant'], msg('Phạm vi không hợp lệ')).default('mine') });
export const getTimesheetPeriodInput = z.object({ month: monthKey });

// ---------- Truy vấn nội bộ ----------
export const listApprovedLeavesInput = z.object({
  fromDate: dateKey,
  toDate: dateKey,
  staffIds: z.array(uuid).max(1000, 'Tối đa 1.000 nhân sự').optional(),
  afterLeaveId: uuid.optional(),
  limit: qInt(1, 1000, 500),
});
export const listStaffOnLeaveInput = z.object({ date: dateKey.optional() });
export const getTimesheetSummariesInput = z.object({ month: monthKey, staffIds: z.array(uuid).max(5000, 'Tối đa 5.000 nhân sự').optional() });
export const getTimesheetSnapshotInput = z.object({ month: monthKey, revision: z.coerce.number({ invalid_type_error: 'Phiên bản phải là số' }).int('Phiên bản phải là số nguyên').min(1, 'Phiên bản chốt từ 1') });

/** Tên RPC: attendance.cmd.<Name> / attendance.qry.<Name>. */
export const RPC = {
  cmd: ['CheckIn', 'CheckOut', 'AdjustWorkDay', 'SubmitLeaveRequest', 'ApproveLeaveRequest', 'RejectLeaveRequest', 'FinalizeTimesheet', 'ReopenTimesheet', 'ExportTimesheet'],
  qry: [
    'GetShiftPolicy',
    'GetMyToday',
    'GetMyTimesheet',
    'GetAnnualLeaveBalance',
    'GetTeamTimesheet',
    'GetWorkDayDetail',
    'ListLeaveRequests',
    'CountPendingLeaves',
    'GetTodayPresenceSummary',
    'GetTimesheetPeriod',
    // nội bộ
    'ListApprovedLeaves',
    'ListStaffOnLeave',
    'GetTimesheetSummaries',
    'GetTimesheetSnapshot',
  ],
} as const;

// ---------- DTO / read model ----------
export interface ShiftPolicyView {
  revision: number;
  start: string;
  end: string;
  graceMin: number;
  fullHours: number;
  halfHours: number;
  lunch: { from: string; to: string; minutes: number };
  workDays: number[];
  holidays: string[];
  lateFree: number;
  latePenalty: number;
  annualLeave: number;
  legend: { code: DayCode; label: string }[];
}
export interface DayEvaluation {
  code: DayCode;
  status: DayStatus;
  credit: number;
  extra: number;
  /** Phút đi muộn (0 khi trong thời gian cho phép). */
  late: number;
  early: number;
}
export interface MyToday {
  serverTime: string;
  workDate: string;
  weekday: number;
  isWorkDay: boolean;
  shift: { start: string; end: string };
  /** false ⇔ CTV (không chấm công). */
  tracked: boolean;
  leaveToday: { leaveId: string; type: LeaveType; label: string; half: boolean } | null;
  record: { checkInAt: string | null; checkOutAt: string | null; place: Place | null; placeLabel: string | null; projectId: string | null; note: string | null } | null;
  evaluation: { code: DayCode; status: DayStatus; lateMinutes: number };
  actions: { canCheckIn: boolean; canCheckOut: boolean; completed: boolean };
}
export interface MonthSummary {
  credit: number;
  extra: number;
  std: number;
  lateCount: number;
  lateMin: number;
  earlyCount: number;
  absent: number;
  missingOut: number;
  leaveDays: number;
  penalty: number;
}
export interface DayCell extends DayEvaluation {
  d: number;
  dk: string;
  weekday: number;
  hours: number | null;
  checkInAt: string | null;
  checkOutAt: string | null;
  placeLabel: string | null;
  adjReason: string | null;
  leaveType: LeaveType | null;
}
export interface AnnualLeaveBalance {
  year: number;
  entitlement: number;
  used: number;
  remaining: number;
}
export interface MyTimesheet {
  month: string;
  period: { status: PeriodStatus };
  days: DayCell[];
  summary: MonthSummary;
  annualLeave: AnnualLeaveBalance;
}
export interface TimesheetPeriodView {
  month: string;
  status: PeriodStatus;
  revision: number;
  finalizedAt: string | null;
  finalizedBy: string | null;
  trigger: FinalizeTrigger | null;
  reopenedAt: string | null;
  reopenReason: string | null;
  payoutStatus: PayoutStatusMirror;
  pendingLeaves: number;
}
export interface TeamTimesheetRow {
  staffId: string;
  code: string;
  name: string;
  teamId: string | null;
  days: (Pick<DayCell, 'd' | 'code' | 'status' | 'credit' | 'late' | 'checkInAt' | 'checkOutAt' | 'placeLabel' | 'adjReason'> & { adjustable: boolean })[];
  summary: MonthSummary;
  canAdjust: boolean;
}
export interface TeamTimesheet {
  month: string;
  period: Pick<TimesheetPeriodView, 'status' | 'revision' | 'finalizedAt' | 'finalizedBy' | 'payoutStatus'>;
  header: { d: number; weekday: number; isWorkDay: boolean; isHoliday: boolean }[];
  rows: TeamTimesheetRow[];
  /** Chỉ tháng hiện tại. */
  today: { checkedIn: number; total: number } | null;
  lateFree: number;
  serverTime: string;
}
export interface WorkDayAdjustmentView {
  credit: AdjustCredit;
  code: 'ĐC' | '½ĐC' | 'V';
  reason: string;
  by: string;
  byName: string | null;
  at: string;
}
export interface WorkDayDetail {
  staffId: string;
  workDate: string;
  evaluation: DayEvaluation & { hours: number | null };
  record: { checkInAt: string | null; checkOutAt: string | null; place: Place | null; placeLabel: string | null; projectId: string | null; note: string | null } | null;
  adjustments: WorkDayAdjustmentView[];
  adjustable: boolean;
  version: number | null;
}
export interface LeaveRequestRow {
  leaveId: string;
  staffId: string;
  staffName: string;
  type: LeaveType;
  typeLabel: string;
  fromDate: string;
  toDate: string;
  half: boolean;
  days: number;
  reason: string;
  status: LeaveStatus;
  decidedBy: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string;
  exceedsAnnualBalance: boolean;
  canDecide: boolean;
  createdAt: string;
  version: number;
}
export interface LeaveRequestList {
  rows: LeaveRequestRow[];
  counts: { pending: number; approved: number; rejected: number; all: number };
  canApprove: boolean;
  page: number;
  limit: number;
}
export interface PendingLeaveCount {
  count: number;
  oldestCreatedAt: string | null;
}
export interface TodayPresence {
  date: string;
  tracked: number;
  checkedIn: number;
  late: number;
}
export interface CheckInResult {
  workDayId: string;
  workDate: string;
  checkInAt: string;
  serverTime: string;
  placeLabel: string;
  lateMinutes: number;
  code: DayCode;
}
export interface CheckOutResult {
  checkOutAt: string;
  serverTime: string;
  code: DayCode;
  credit: number;
  extra: number;
  earlyMinutes: number;
}
export interface SubmitLeaveResult {
  leaveId: string;
  days: number;
  exceedsAnnualBalance: boolean;
  annual: { used: number; entitlement: number };
}
export interface AdjustWorkDayResult {
  code: DayCode;
  credit: number;
  status: DayStatus;
  version: number;
}
/** Kết quả ExportTimesheet: gateway trả nguyên `content` dạng text/csv (UTF-8 có BOM), tệp `fileName`. */
export interface TimesheetExportFile {
  fileName: string;
  contentType: string;
  content: string;
  rowCount: number;
}
/** Kết quả FinalizeTimesheet / ReopenTimesheet. */
export type TimesheetPeriodResult = TimesheetPeriodView & { version: number };
// Nội bộ
export interface ApprovedLeave {
  leaveId: string;
  staffId: string;
  type: LeaveType;
  fromDate: string;
  toDate: string;
  half: boolean;
  version: number;
}
export interface StaffOnLeave {
  staffId: string;
  leaveId: string;
  type: LeaveType;
  half: boolean;
}
export type LeaveByType = Record<LeaveType, number>;
export interface TimesheetSummaryRow extends MonthSummary {
  staffId: string;
  contractType: 'official' | 'probation' | 'ctv';
  employmentStatus: 'active' | 'left';
  leftAt: string | null;
  leaveByType: LeaveByType;
  summaryRevision: number;
}
export interface TimesheetSummaries {
  month: string;
  periodStatus: PeriodStatus;
  periodRevision: number;
  policyRevision: number;
  lateFree: number;
  latePenalty: number;
  rows: TimesheetSummaryRow[];
}
export interface FinalizedTimesheetRow extends TimesheetSummaryRow {
  code: string;
  name: string;
  adjustedDays: number;
}

// ---------- Sự kiện ----------
export const EVENTS = {
  workDayCheckedIn: 'attendance.workDay.checkedIn',
  workDayCheckedOut: 'attendance.workDay.checkedOut',
  workDayAdjusted: 'attendance.workDay.adjusted',
  leaveRequested: 'attendance.leave.requested',
  leaveApproved: 'attendance.leave.approved',
  leaveRejected: 'attendance.leave.rejected',
  staffTimesheetRecalculated: 'attendance.staffTimesheet.recalculated',
  // Bên tham gia saga duyệt kỳ chi trả (commission điều phối)
  timesheetFinalized: 'attendance.timesheet.finalized',
  timesheetFinalizeRejected: 'attendance.timesheet.finalizeRejected',
  timesheetReopened: 'attendance.timesheet.reopened',
  /** Phản hồi yêu cầu mở lại (bù trừ saga) khi không cần mở lại — commission ghi nhận release thay vì gửi lại. */
  timesheetReleaseSkipped: 'attendance.timesheet.releaseSkipped',
  timesheetExported: 'attendance.timesheet.exported',
} as const;
export type EventType = (typeof EVENTS)[keyof typeof EVENTS];

/** aggregateId = workDayId. */
export interface WorkDayCheckedIn {
  staffId: string;
  workDate: string;
  checkInAt: string;
  place: Place;
  projectId: string | null;
  placeLabel: string;
  note: string | null;
  isWorkDay: boolean;
  lateMinutes: number;
  clientSkewMs: number | null;
}
export interface WorkDayCheckedOut {
  staffId: string;
  workDate: string;
  checkInAt: string;
  checkOutAt: string;
  earlyMinutes: number;
  hours: number;
  code: DayCode;
  credit: number;
  extra: number;
  acknowledgedEarly: boolean;
}
/** Thao tác nhạy cảm "Điều chỉnh ngày công". */
export interface WorkDayAdjusted {
  staffId: string;
  workDate: string;
  credit: AdjustCredit;
  code: 'ĐC' | '½ĐC' | 'V';
  reason: string;
  previous: { code: DayCode; credit: number; status: DayStatus };
  adjustedBy: string;
  adjustedAt: string;
}
/** aggregateId = leaveId. */
export interface LeaveRequested {
  leaveId: string;
  staffId: string;
  teamId: string | null;
  type: LeaveType;
  fromDate: string;
  toDate: string;
  half: boolean;
  days: number;
  reason: string;
  exceedsAnnualBalance: boolean;
  createdAt: string;
}
export interface LeaveApproved {
  leaveId: string;
  staffId: string;
  teamId: string | null;
  type: LeaveType;
  fromDate: string;
  toDate: string;
  half: boolean;
  days: number;
  /** Công theo loại (1 / 0). */
  credit: number;
  code: DayCode;
  decidedBy: string;
  decidedAt: string;
}
export interface LeaveRejected {
  leaveId: string;
  staffId: string;
  type: LeaveType;
  fromDate: string;
  toDate: string;
  half: boolean;
  decidedBy: string;
  decidedAt: string;
  decisionNote: string;
}
/** aggregateId = '<staffId>:<month>'; gộp tối đa một sự kiện mỗi (nhân sự, tháng) trong một transaction. */
export interface StaffTimesheetRecalculated {
  staffId: string;
  month: string;
  summaryRevision: number;
  policyRevision: number;
  periodStatus: PeriodStatus;
  cause: RecalcCause;
  summary: MonthSummary;
  evaluatedForDate: string;
}
/** Yêu cầu từ saga duyệt kỳ chi trả (commission.payout.timesheetFinalizeRequested) — phản hồi mang lại khoá. */
export interface TimesheetSagaRequestRef {
  idempotencyKey: string; // '<payoutId>:finalize:<revision>'
  payoutId: string;
}
/** aggregateId = periodId. Nguồn chính thức cho bảng thu nhập. Vượt ngưỡng broker ⇒ rows = [], rowsTruncated = true. */
export interface TimesheetFinalized {
  month: string;
  revision: number;
  trigger: FinalizeTrigger;
  request: TimesheetSagaRequestRef | null;
  finalizedBy: string | null;
  finalizedAt: string;
  policy: {
    revision: number;
    start: string;
    end: string;
    graceMin: number;
    fullHours: number;
    halfHours: number;
    workDays: number[];
    holidaysInMonth: string[];
    lateFree: number;
    latePenalty: number;
    annualLeave: number;
  };
  std: number;
  rows: FinalizedTimesheetRow[];
  rowsTruncated: boolean;
  rowCount: number;
  checksum: string;
}
/**
 * Từ chối yêu cầu chốt của saga: PERIOD_NOT_ENDED (tháng chưa kết thúc); CANCELLED (đã có yêu cầu mở lại / bù trừ cùng khoá —
 * yêu cầu chốt đến muộn không được thực hiện); PERIOD_REOPENED (khoá này đã chốt nhưng kỳ đã được mở lại — không phát lại ảnh chụp cũ).
 */
export interface TimesheetFinalizeRejected {
  month: string;
  request: TimesheetSagaRequestRef;
  reasonCode: 'PERIOD_NOT_ENDED' | 'CANCELLED' | 'PERIOD_REOPENED' | (string & {});
  message: string;
}
export interface TimesheetReopened {
  month: string;
  /** Revision ảnh chụp vừa được mở lại. */
  revision: number;
  reason: string;
  reopenedBy: string | null;
  reopenedAt: string;
  /** Có khi mở lại do bù trừ saga (commission.payout.timesheetReleaseRequested). */
  request: { idempotencyKey: string; releaseKey: string; payoutId: string } | null;
}
/**
 * aggregateId = periodId (payoutId khi tháng không hợp lệ). Yêu cầu mở lại không cần thực hiện: kỳ không do khoá chốt này chốt
 * (chưa chốt / chốt thủ công / đã mở lại) ⇒ NOT_FINALIZED_BY_REQUEST; kỳ chi trả đã duyệt / đã chi ⇒ PAYOUT_LOCKED.
 */
export interface TimesheetReleaseSkipped {
  month: string;
  request: { idempotencyKey: string; releaseKey: string; payoutId: string };
  reasonCode: 'NOT_FINALIZED_BY_REQUEST' | 'PAYOUT_LOCKED' | 'INVALID_INPUT';
  message: string;
}
/** Thao tác nhạy cảm "Xuất bảng công". */
export interface TimesheetExported {
  month: string;
  teamId: string | null;
  scope: 'own' | 'team' | 'all';
  rowCount: number;
  fileName: string;
  exportedBy: string;
}

export interface EventPayloads {
  [EVENTS.workDayCheckedIn]: WorkDayCheckedIn;
  [EVENTS.workDayCheckedOut]: WorkDayCheckedOut;
  [EVENTS.workDayAdjusted]: WorkDayAdjusted;
  [EVENTS.leaveRequested]: LeaveRequested;
  [EVENTS.leaveApproved]: LeaveApproved;
  [EVENTS.leaveRejected]: LeaveRejected;
  [EVENTS.staffTimesheetRecalculated]: StaffTimesheetRecalculated;
  [EVENTS.timesheetFinalized]: TimesheetFinalized;
  [EVENTS.timesheetFinalizeRejected]: TimesheetFinalizeRejected;
  [EVENTS.timesheetReopened]: TimesheetReopened;
  [EVENTS.timesheetReleaseSkipped]: TimesheetReleaseSkipped;
  [EVENTS.timesheetExported]: TimesheetExported;
}
type AssertNever<T extends never> = T;
/** Lỗi biên dịch nếu có sự kiện trong EVENTS chưa khai báo payload. */
export type _AllEventsHavePayload = AssertNever<Exclude<EventType, keyof EventPayloads>>;
